import { config } from '@/config';
import { isEmbedBundle, withEmbedBasename } from '@/helpers/embed';

type BokadirektPayReconcileParams = {
  resourceType: 'booking' | 'bundle';
  resourceId: string | number;
  checkoutRef?: string;
  session?: { id?: string | null; result?: string };
};

function buildBokadirektPayReconcileRoute(params: BokadirektPayReconcileParams): string {
  const { resourceType, resourceId, checkoutRef, session } = params;
  const base = `validate-bokadirekt-pay/${resourceType}?resourceId=${resourceId}&checkoutRef=${checkoutRef}`;
  return session ? `${base}&sessionId=${session.id}&sessionResult=${session.result}` : base;
}

/**
 * The bokadirekt-pay reconciliation route as an in-SPA path, for a client-side
 * `history.replace` into the validate page. Same query as
 * {@link buildBokadirektPayReconcileUrl}, without origin or embed basename.
 */
export function buildBokadirektPayReconcilePath(params: BokadirektPayReconcileParams): string {
  return `/${buildBokadirektPayReconcileRoute(params)}`;
}

/**
 * The bokadirekt-pay reconciliation URL — where the client lands after an Adyen
 * payment so the backend can verify and finalise the booking/bundle.
 *
 * Single definition of this URL, which is otherwise hand-assembled at every
 * completion site. The optional `session` carries the with/without-3DS
 * distinction: present when an Adyen session was mounted (its result must be
 * reconciled), absent when the booking was already authorised.
 *
 * In the booking-widget SPA the client lands on that SPA's own validate route,
 * so the widget's pay popup and a Linked Session finish paying without loading
 * the marketplace SPA.
 */
export function buildBokadirektPayReconcileUrl(params: BokadirektPayReconcileParams): string {
  return `${withEmbedBasename(config.baseUrl)}${buildBokadirektPayReconcileRoute(params)}`;
}

/**
 * Where a failed reconcile lands when the backend names no destination. The
 * booking-widget SPA has no `/` route, so a Linked Session would blank out on
 * the marketplace fallback; its checkout is the nearest surface that rebuilds
 * itself from storage and lets the visitor retry. The pay popup reaches neither
 * — it posts the error to the host and closes.
 */
export function getBokadirektPayErrorFallbackPath(): string {
  return isEmbedBundle() ? '/booking/checkout' : '/';
}
